import React, { useEffect, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { Box, Container, Typography, Alert, Button, CircularProgress } from "@mui/material";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";

/**
 * Public page: confirm ED claim domain email via magic link token.
 * Does not display the claimant email publicly.
 */
export default function EdClaimEmailVerifyPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState("loading");
  const [verificationId, setVerificationId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("Missing verification token.");
      return;
    }

    let cancelled = false;
    const fn = firebase.functions().httpsCallable("confirmEdClaimDomainEmail");
    fn({ token })
      .then((res) => {
        if (cancelled) return;
        const vid = res.data?.verificationId || null;
        const locId = res.data?.locationId || null;
        setVerificationId(vid);
        setStatus("verified");
        if (vid && locId) {
          try {
            sessionStorage.setItem(`ed_claim_domain_ver_${locId}`, vid);
          } catch (storageErr) {
            console.warn("[EdClaimEmailVerify] sessionStorage write failed:", storageErr);
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[EdClaimEmailVerify] confirm failed:", err);
        setStatus("error");
        setErrorMessage(err?.message || "Verification failed or link expired.");
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Email verification
      </Typography>

      {status === "loading" ? (
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <Typography variant="body1">Confirming your email…</Typography>
        </Box>
      ) : null}

      {status === "verified" ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            Your facility email is verified. Return to the CareMap to complete your ED claim.
          </Alert>
          {verificationId ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Verification reference: <strong>{verificationId}</strong>
            </Typography>
          ) : null}
          <Button component={RouterLink} to="/" variant="contained">
            Back to map
          </Button>
        </>
      ) : null}

      {status === "error" ? (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
          <Button component={RouterLink} to="/" variant="outlined">
            Back to map
          </Button>
        </>
      ) : null}
    </Container>
  );
}
